import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Minus, Plus, ChevronLeft, ShoppingBag } from 'lucide-react';
import { useApp } from '../context/AppContext';
import Navbar from './Navbar';
import { toast } from 'sonner';

const Cart = () => {
  const navigate = useNavigate();
  const { t, cart, removeFromCart, updateCartQuantity, getCartTotal, clearCart } = useApp();

  const getItemPrice = (item) => {
    const size = item.product?.sizes?.find(s => s.ml === item.size_ml);
    return size?.price || item.product?.price || 0;
  };

  const total = getCartTotal();

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Navbar />
        <div className="container mx-auto px-4 md:px-8 py-16">
          <div className="text-center">
            <ShoppingBag size={64} className="mx-auto text-[#57534E] mb-6" />
            <h1 className="font-heading text-3xl text-white mb-4">{t('cart')}</h1>
            <p className="text-[#A8A29E] mb-8">{t('empty_cart')}</p>
            <Link to="/home" className="btn-primary inline-block">
              {t('continue_shopping')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      
      <div className="container mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-heading text-3xl text-white">{t('cart')}</h1>
          <button
            onClick={() => { clearCart(); toast.success(t('success')); }}
            className="text-[#A8A29E] hover:text-red-500 text-sm transition-colors"
            data-testid="clear-cart-btn"
          >
            {t('delete')}
          </button>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.map((item, index) => {
              const price = getItemPrice(item);
              const itemTotal = price * item.quantity;

              return (
                <div 
                  key={`${item.product_id}-${item.size_ml}`}
                  className="glass p-4 md:p-6 rounded-xl flex gap-4"
                  data-testid={`cart-item-${index}`}
                >
                  {/* Product Image */}
                  <Link 
                    to={`/product/${item.product_id}`}
                    className="w-24 h-24 md:w-32 md:h-32 flex-shrink-0 rounded-lg overflow-hidden bg-[#121212]"
                  >
                    <img 
                      src={item.product?.image_url} 
                      alt={item.product?.name}
                      className="w-full h-full object-cover"
                    />
                  </Link>

                  {/* Product Info */}
                  <div className="flex-1 flex flex-col justify-between">
                    <div>
                      <p className="text-[#D4AF37] text-xs uppercase tracking-wider">
                        {item.product?.brand}
                      </p>
                      <Link 
                        to={`/product/${item.product_id}`}
                        className="font-heading text-white text-lg hover:text-[#D4AF37] transition-colors"
                      >
                        {item.product?.name}
                      </Link>
                      <p className="text-[#A8A29E] text-sm">{item.size_ml}ml</p>
                    </div>

                    <div className="flex items-center justify-between mt-2">
                      {/* Quantity Controls */}
                      <div className="flex items-center gap-3 glass rounded-full px-3 py-1">
                        <button
                          onClick={() => updateCartQuantity(item.product_id, item.size_ml, item.quantity - 1)}
                          className="text-white hover:text-[#D4AF37]"
                        >
                          <Minus size={16} />
                        </button>
                        <span className="text-white w-6 text-center">{item.quantity}</span>
                        <button
                          onClick={() => updateCartQuantity(item.product_id, item.size_ml, item.quantity + 1)}
                          className="text-white hover:text-[#D4AF37]"
                        >
                          <Plus size={16} />
                        </button>
                      </div>

                      {/* Price & Remove */}
                      <div className="flex items-center gap-4">
                        <span className="text-[#D4AF37] font-bold">
                          €{itemTotal.toFixed(2)}
                        </span>
                        <button
                          onClick={() => { 
                            removeFromCart(item.product_id, item.size_ml);
                            toast.success(t('removed_from_cart'));
                          }}
                          className="text-[#57534E] hover:text-red-500 transition-colors"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="glass p-6 rounded-xl sticky top-24">
              <h2 className="font-heading text-xl text-white mb-6">{t('order_summary')}</h2>
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-[#A8A29E]">
                  <span>{t('subtotal')}</span>
                  <span>€{total.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-[#A8A29E]">
                  <span>{t('shipping')}</span>
                  <span>{total >= 50 ? t('free') : '€4.95'}</span>
                </div>
                <div className="border-t border-[#2C1B18] pt-4 flex justify-between text-white">
                  <span className="font-bold">{t('total')}</span>
                  <span className="font-bold text-[#D4AF37] text-xl">
                    €{(total >= 50 ? total : total + 4.95).toFixed(2)}
                  </span>
                </div>
              </div>

              <p className="text-[#57534E] text-xs mb-4">
                {total < 50 ? t('add_for_free_shipping').replace('{amount}', (50 - total).toFixed(2)) : t('free_shipping_included')}
              </p>

              <button
                onClick={() => navigate('/checkout')}
                className="btn-primary w-full"
                data-testid="checkout-btn"
              >
                {t('checkout')}
              </button>

              <Link 
                to="/home"
                className="flex items-center justify-center gap-2 text-[#A8A29E] hover:text-[#D4AF37] mt-4 transition-colors"
              >
                <ChevronLeft size={16} />
                {t('continue_shopping')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
